<template>
  <div class="main-pc-sign-pdf">
    <!-- PDF -->
    <div
      class="content-pdf"
      :class="{ 'content-pdf__full-height': !showSignNow && !showSignLater }"
    >
      <VuePdfApp :pdf="pdf" />
    </div>

    <!-- Footer Buttons -->
    <div
      v-if="showSignNow || showSignLater"
      class="content-sign-buttons"
    >
      <f7-button
        v-if="showSignNow"
        fill
        color="green"
        @click="openSignNowPopup"
      >
        {{ $t('common.sign') }}
      </f7-button>
      <f7-button
        v-if="showSignLater"
        fill
        color="pink"
        @click="emit('sign-later-click')"
      >
        {{ $t('common.signLater') }}
      </f7-button>
    </div>

    <f7-popup
      class="main-pc-sign-pdf-popup"
      close-on-escape
      :opened="signPopupOpened"
      @popup:closed="signPopupOpened = false"
    >
      <f7-page>
        <template #fixed>
          <TheNavbar
            :nav-title="popupTitle"
            class="at-navbar secondary-navbar"
            :popup-close-btn-enable="true"
          />
        </template>

        <!-- Setting display flex to page content to allow the sign part to fill the remaining -->
        <template v-if="showNifInput || showEmailsInput">
          <!-- Emails and NIF -->
          <h3 class="margin-left margin-bottom-half">
            Datos
          </h3>

          <template v-if="showEmailsSearch || showEmailsInput">
            <!-- List of emails -->
            <UCSearchEntityERP
              v-if="showEmailsSearch"
              class="margin-left margin-right margin-bottom-half"
              title="Búsqueda de emails"
              placeholder="Buscar por dirección de correo electrónico"
              search-text="Buscando emails"
              search-cancellation-text="Búsqueda de emails cancelada…"
              search-not-found-text="No se han encontrado emails"
              :property-entity-shown-input="['email']"
              :property-entity-shown-list-title="['email']"
              :disabled="!emails || emails.length === 0"
              :default-etities-list="emailsObject"
              :autofocus-on-deselection="true"
              :multi-selection="true"
              @selected-elements="OnDefaultEmailsSelection"
            />

            <div
              v-if="showEmailsInput"
              class="content-sign__data margin-left margin-right margin-bottom-half"
            >
              <label class="margin-left-half">
                Correos electrónicos alternativos
              </label>
              <input
                v-model="emailsInputValue"
                class="content-sign__data--input padding-left-half padding-right-half"
                type="text"
              >
              <span class="content-sign__emails-help">
                Si quiere añadir varios correos electrónicos, deben estar separados por coma.
              </span>
            </div>
          </template>

          <template v-if="showNifInput">
            <div class="content-sign__data margin-left margin-right margin-bottom-half">
              <label class="margin-left-half">
                NIF del firmante
              </label>
              <input
                v-model="signatoryNif"
                class="content-sign__data--input padding-left-half padding-right-half"
                type="text"
                pattern="([a-z]|[A-Z]|[0-9])[0-9]{7}([a-z]|[A-Z]|[0-9])"
                minlength="9"
                maxlength="9"
              >
            </div>
          </template>
        </template>

        <!-- Signature -->
        <h3
          class="margin-left no-margin-bottom"
          :class="{ 'no-margin-top': showNifInput || showEmailsInput }"
        >
          Firma
        </h3>

        <div class="display-flex justify-content-center">
          <HIFormInputSignature
            :are-buttons-rounded="true"
            @new-signature="(value) => signature = value"
            @signature-cleared="signature = ''"
          />
        </div>

        <!-- Confirm -->
        <div class="margin">
          <f7-button
            fill
            color="green"
            :disabled="signature === '' || signatoryNif === ''"
            @click="handleSignConfirmation"
          >
            Confirmar firma
          </f7-button>
        </div>
      </f7-page>
    </f7-popup>
  </div>
</template>

<script setup>
// @ts-check

import { computed, ref } from 'vue';

import VuePdfApp from 'vue3-pdf-app';
import { HiNotification } from '@/js/common/HINotificationSupport';

import TheNavbar from '@/components/TheNavbar.vue';
import UCSearchEntityERP from '@/components/common/UCSearchEntityERP.vue';

import 'vue3-pdf-app/dist/icons/main.css';
import HIFormInputSignature from '@/components/common/HIFormInputSignature.vue';

/** @import { PropType, Ref } from 'vue' */

const props = defineProps({
  /** Stores the pdf reference (it may be a url) */
  pdf: { type: [String, ArrayBuffer], default: null },
  /** Stores if the 'Sign' button should be displayed */
  showSignNow: { type: Boolean, default: true },
  /** Stores if the 'Sign Later' button should be displayed */
  showSignLater: { type: Boolean, default: true },
  /** Stores the popup title */
  popupTitle: { type: String, default: null },
  /** Stores if the UCSearchEntityERP of emails should be displayed in the popup */
  showEmailsSearch: { type: Boolean, default: false },
  /** Stores if the emails input should be displayed in the popup */
  showEmailsInput: { type: Boolean, default: false },
  /** Stores if the NIF input should be displayed in the popup */
  showNifInput: { type: Boolean, default: false },
  /** Stores the emails that will be used in the UCSearchEntityERP component. If empty, it will be disabled */
  emails: {
    /** @type {PropType<string[]>} */
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'sign-now-click',
  'sign-later-click',
  'confirm-signature',
]);

/** Stores if the popup should be displayed */
const signPopupOpened = ref(false);
/** Stores the NIF of the signatory */
const signatoryNif = ref('');
/** Stores a list of emails that are written by the user */
const emailsInputValue = ref('');
/** Stores the signature */
const signature = ref('');

/**
 * Stores a list of selected emails of the delivery note.
 * @type {Ref<any[]>}
 */
const selectedEmails = ref([]);

/**
 * Transforms the emails array to an object.
 * It's needed to be passed as an object because UCSearchEntityERP requires an object, but it can't be an array of Strings
 */
const emailsObject = computed(
  () => (props.emails ? props.emails.map((email) => ({ email })) : []),
);

/** A method that is called when user clicks on 'Sign Now' */
function openSignNowPopup() {
  signPopupOpened.value = true;
  emit('sign-now-click');
}

/** A method that is called when the signature is confirmed */
function handleSignConfirmation() {
  signPopupOpened.value = false;
  emit(
    'confirm-signature',
    signature.value,
    signatoryNif.value.trim(),
    emailsInputValue.value.trim(),
    selectedEmails.value,
  );
}

/**
 * A method that is called when user confirms the emails selection in the popup.
 * @param {any[]} selectedItems - The selected items.
 */
function OnDefaultEmailsSelection(selectedItems) {
  selectedEmails.value = selectedItems.length > 0 ? selectedItems.map((item) => item.email) : [];
  if (selectedEmails.value.length > 0) {
    // Show this toast because UCSearchEntityERP doesn't show in any way that the emails have been selected
    HiNotification.Toast(`Se han seleccionado correctamente ${selectedEmails.value.length} emails`);
  }
}
</script>

<style scoped lang="scss">
.main-pc-sign-pdf {
  .content-pdf {
    height: calc(100% - 70px);

    // If there are no buttons, pdf will have 100% of height
    &__full-height {
      height: 100%;
    }
  }

  .content-sign-buttons {
    height: 70px;
    display: flex;
    flex-wrap: wrap;

    .button {
      height: 100%;
      flex: 1;
      min-width: 150px;
      border-radius: 0;
    }
  }
}

.main-pc-sign-pdf-popup {
  --search-title-color: #000;

  .content-sign {
    &__data {
      &--input {
        background: #fff;
        border: 1px solid var(--his-silver);
        border-radius: 8px;
        display: flex;
        align-items: center;
        width: 100%;
        height: 36px;
        color: #24253d;
        font-size: 14px;
      }

      .input-with-value {
        color: #000;
      }
    }

    &__emails-help {
      opacity: 0.5;
      font-size: 12px;
    }
  }
}
</style>
